"use client";

import { useLayoutEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import type { SectionId } from "@/hooks/use-active-section";

const ENTER_THRESHOLD = 0.08;
const FILL_DURATION_MS = 100;

type NavUnderlineProps = {
  isActive: boolean;
  isHovered: boolean;
  progress: number;
  activeId: SectionId;
  className?: string;
};

export function NavUnderline({
  isActive,
  isHovered,
  progress,
  activeId,
  className,
}: NavUnderlineProps) {
  const reducedMotion = useReducedMotion();
  const [fillScale, setFillScale] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  useLayoutEffect(() => {
    if (!isActive) {
      setFillScale(null);
      setIsAnimating(false);
      return;
    }

    if (progress > ENTER_THRESHOLD || reducedMotion) {
      setFillScale(null);
      setIsAnimating(false);
      return;
    }

    setIsAnimating(false);
    setFillScale(0);

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setIsAnimating(true);
        setFillScale(1);
      });
    });

    const timer = window.setTimeout(() => {
      setIsAnimating(false);
      setFillScale(null);
    }, FILL_DURATION_MS);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(timer);
    };
  }, [activeId, isActive, reducedMotion]);

  const scrollScale = 1 - progress;
  const scale = isHovered ? 1 : !isActive ? 0 : fillScale !== null ? fillScale : scrollScale;

  const isEntranceFill = fillScale !== null;
  const shouldAnimate = !reducedMotion && (isHovered || isAnimating);

  return (
    <span
      className={cn(
        "absolute right-0 left-0 h-px bg-foreground",
        shouldAnimate &&
          "motion-safe:transition-transform motion-safe:duration-100 motion-safe:ease-out",
        className,
      )}
      style={{
        transform: `scaleX(${scale})`,
        transformOrigin: isEntranceFill ? "left center" : "right center",
      }}
    />
  );
}
